/**
 * external dependencies
 */
import { TabulatorFull, Options, CellComponent } from 'tabulator-tables';

/**
 * internal dependencies
 */
import useAuth from '../../../hooks/auth-hooks';
import { ICardGroupInsertUpdate } from '../../../models/cardgroup.interface';

/**
 * Displays the card group list table on the card group page
 * 
 * @param table the parent HTML element that the table is to be displayed in
 * @param data the data that is to be displayed in the table, this is the api response from the card group list api.
 * 
 * @returns the table object that is created after parsing the data and displaying it in table format.
 */
export const displayCardGroupTable = (table: HTMLElement, data: ICardGroupInsertUpdate[]): TabulatorFull => {
  const { accessType } = useAuth()!;

  /**
   * Creates a link button for each row in the table
   * 
   * @param {CellComponent} cell the cell component
   * 
   * @returns string
   */
  const link_button = (cell: CellComponent, formatterParams: any) => {
    const { cardGroupId, cardGroupName, customerId } = cell.getData() as ICardGroupInsertUpdate;
    const userPrev = accessType === 'E' ? 'edit' : 'view';

    const editLink = `<a class='aafp_btn-table btn_theme-primary'' 
                        href='/card-group/${userPrev}-card-group?groupAction=${userPrev}&groupID=${cardGroupId}' 
                        data-toggle="tooltip" 
                        data-placement="bottom" 
                        title="${userPrev.charAt(0).toUpperCase() + userPrev.slice(1)} ${cardGroupName} Card Group">
                          <i class="${userPrev === 'edit' ? 'fas fa-pen' : 'far fa-eye'}"></i>
                      </a>`;

    const linkCardGroup = `<a class='aafp_btn-table btn_theme-outline' 
                            href='/card-group/terminal-card-group/?linkAction=cardgroup&cardGroupID=${cardGroupId}&customerId=${customerId}' 
                            data-toggle="tooltip" 
                            data-placement="bottom" 
                            title="Link ${cardGroupName} Card Group"><i class="fas fa-link"></i>
                          </a>`;

    return `${editLink} ${linkCardGroup}`;
  }

  /**
   * Formats the payment method column
   * 
   * @param {CellComponent} cell the cell component
   * 
   * @returns string
   */
  const paymentFormat = (cell: CellComponent, formatterParams: any) => {
    const { paymentMethod } = cell.getData() as ICardGroupInsertUpdate;
    return paymentMethod === '0' ? 'Pay At Pump' : 'Invoice';
  }

  /**
   * Formats the margin column
   * 
   * @param {CellComponent} cell the cell component
   * 
   * @returns string
   */
  const formatMargin = (cell: CellComponent, formatterParams: any) => {
    const { marginCents } = cell.getData() as ICardGroupInsertUpdate;
    const newMargin = parseFloat(marginCents.toString()).toFixed(6);
    return newMargin;
  }

  /**
   * Formats the hose type column
   * 
   * @param {CellComponent} cell the cell component
   * 
   * @returns string
   */
  const hoseTypeFormat = (cell: CellComponent, formatterParams: any) => {
    const { hoseId } = cell.getData() as ICardGroupInsertUpdate;
    return hoseId === '0' ? 'Autogas/Cylinder' : hoseId === '1' ? 'Autogas' : 'Cylinder Fill';
  }

  const options: Options = {
    index: "cardGroupId",
    data: data,
    placeholder: "No Data Available",
    placeholderHeaderFilter: "No Matching Data",
    pagination: true,
    paginationMode: "local",
    paginationSize: 10,
    paginationSizeSelector: [25, 50, 75],
    paginationCounter: "rows",
    selectableRows: false,
    columns: [
      { title: "cardGroupId", field: "cardGroupId", visible: false },
      { title: "Card Group Name", field: "cardGroupName", resizable: false, vertAlign: 'middle' },
      { title: "Customer Name", field: "customerName", resizable: false, vertAlign: 'middle' },
      { title: "Payment Method", field: "paymentMethod", formatter: paymentFormat, resizable: false, vertAlign: 'middle' },
      { title: "Margin", field: "marginCents", formatter: formatMargin, resizable: false, vertAlign: 'middle' },
      { title: "Hose Type", field: "hoseId", formatter: hoseTypeFormat, resizable: false, vertAlign: 'middle' },
      { title: '', field: "LinkButton", formatter: link_button, width: 80, hozAlign: 'right', headerSort: false, resizable: false },
    ]
  };

  const cardGroupTableGrid = new TabulatorFull(table, options);
  return cardGroupTableGrid;
};